import React from 'react';
import Easyfa from 'easyfa';

import PropTypes from 'prop-types';
import {filterNodeProperties} from '../../../../utilities/filter-node-properties';
import {withRouter} from 'react-router-dom';
import {handleEvent} from '../../../../events/event-handler';

const getAnimationNode = function (node) {
    const keys = ['title', 'duration', 'from', 'relative', 'repeat', 'start', 'to', 'motion', 'attribute'];
    return filterNodeProperties(node, keys);
};

const getAnimationGroupNode = function (node) {
    const keys = ['title', 'duration', 'from', 'relative', 'process', 'repeat', 'start', 'to', 'motion'];
    return filterNodeProperties(node, keys, {steps: []});
};

const getDataFromNode = function (node) {
    const keys = ['id', 'title', 'src', 'x', 'y', 'visible', 'start', 'loop', 'required', 'visible', 'alpha', 'bgalpha', 'width', 'zIndex', 'height'];
    return filterNodeProperties(node, keys);
};

const getStateFromProps = function (props = {}) {
    const event_nodes = props.node.childrenNamed('event_node') || [];
    const animationGroupNode = props.node.childNamed('animation_group_node') || null;
    let animation = null;


    if (animationGroupNode) {
        animation = getAnimationGroupNode(animationGroupNode);
        animation.steps = animationGroupNode.childrenNamed('animation_node').map(step => {
            return getAnimationNode(step);
        });
    }

    return {
        ...getDataFromNode(props.node),
        event_nodes,
        animation: animation,
        timeouts: [],
        firstPlay: true,
    };
};

class AnimatedImageNode extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            ...getStateFromProps(props),
        };

        this.startAnimation = this.startAnimation.bind(this);
        this.startAnimationEvent = this.startAnimationEvent.bind(this);
        this.stopAnimationEvent = this.stopAnimationEvent.bind(this);

    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.node === nextProps.node) {
            this.setState({
                ...getStateFromProps(nextProps)
            });
        }
    }

    componentDidMount() {
        document.addEventListener('jre-animation-start', this.startAnimationEvent);
        document.addEventListener('jre-animation-stop', this.stopAnimationEvent);

        // animation exists when animation_group_nodes/animation_nodes are present
        // Checks if the component contains valid animation properties
        if(this.state.animation) {
            const stepsList = this.generateAnimationSteps();
            if (stepsList.length > 0) {
                // We need to know the visibility/opacity state of the image right away before promise steps run below.
                this.setState({opacity: stepsList[0].opacity});

                let resolvePromise = Promise.resolve();
                // Run the animation steps one at a time.
                stepsList.forEach(animation => {
                    resolvePromise = resolvePromise.then(this.delay.bind(this, animation));
                });
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener('jre-animation-start', this.startAnimationEvent);
        document.removeEventListener('jre-animation-stop', this.stopAnimationEvent);

        this.state.timeouts.forEach(clearTimeout); // if animation_group_nodes don't exist, timeouts is []
        this.aPng = null;

    }

    // starts an animation onLoad of the apng
    startAnimation() {
        if (this.state.loop === '0') {
            this.aPng.one();
        } else {
            this.aPng.play();
        }
        this.onStartAnimation();
    }

    // stops an animation, targeted by an event
    stopAnimationEvent(e) {
        if ((typeof this.props.node.attr.id !== undefined) && (typeof e.detail.target !== undefined)
            && this.props.node.attr.id.toLowerCase() === e.detail.target.toLowerCase()) {
            this.aPng.stop();
            this.onStopAnimation();
        }
    }

    // starts an animation, targeted by an event
    startAnimationEvent(e) {
        if ((typeof this.props.node.attr.id !== undefined) && (typeof e.detail.target !== undefined)
            && this.props.node.attr.id.toLowerCase() === e.detail.target.toLowerCase()) {

            // When starting an animation through an event, the visibility may not be enabled by default
            // Also, firstPlay is reset here to handle the flow of start -> stop -> start (MO.IMMG.3.0.2 - 142)
            this.setState({visible: '1', firstPlay: true}, () => {
                if (this.state.loop === '0') {
                    this.aPng.one();
                } else {
                    this.aPng.play();
                }
                this.onStartAnimation();
            });
        }
    }

    // operations to perform when starting the apng for the first time
    onStartAnimation() {
        const {event_nodes} = this.state;
        event_nodes.forEach(function (event) {
            if (event.attr.name === 'onanimatedimagestart') {
                handleEvent(event);
            }
        });
    }

    // operations to perform when stopping the apng
    onStopAnimation() {
        const {event_nodes} = this.state;
        event_nodes.forEach(function (event) {
            if (event.attr.name === 'onanimatedimagestop') {
                handleEvent(event);
            }
        });
    }

    // Operations to perform once an iteration of an apng is completed
    // It seems to be slightly delayed in Easyfa
    onAnimationIterationComplete() {
        let {loop, event_nodes, firstPlay} = this.state;

        // The library for Easyfa's onLoopEnd callback takes place an extra time before the initial iteration actually completes.
        // We need this check to ensure the first loop has actually completed before running onRepeatAnimation
        if (this.aPng.player._numPlays !== 0) {
            // if loop is off, run stop operations
            if (loop === '0') {
                this.aPng.stop();
                this.onStopAnimation();

            } else { // otherwise handle repeat events, and continue
                event_nodes.forEach(function (event) {
                    if (event.attr.name === 'onanimatedimagerepeat') {
                        handleEvent(event);
                    }
                });
            }

            // According to JRE specs, onanimatedimagecomplete must run when an apng iteration completes for the first time
            if (firstPlay) {

                // If there are multiple animation_image_nodes on the same slide, it seems that each animation_image_node one disappears once it is completed once.
                // Ideally it should be disabled by an event, but this doesn't seem to be the case
                // ex) MO.IMMG.3.0.2 - 142
                const visible = this.props.multiAnimatedImageNodes ? '0' : '1'; // 0 is invisible, 1 is visible

                event_nodes.forEach(function (event) {
                    if (event.attr.name === 'onanimatedimagecomplete') {
                        handleEvent(event);
                    }
                });
                this.setState({firstPlay: false, visible});
            }
        }
    }


    translateX(from, to) {
        from = parseInt(from);
        to = parseInt(to);

        if (from > to) {
            return `translate(-${from - to}px)`;
        } else if (from < to) {
            return `translate(${to - from}px)`;
        } else {
            return 'translate(0px)';
        }
    }

    generateAnimationSteps() {
        const stepsList = [];

        this.state.animation.steps.forEach((step) => {
            const stepsOb = {};
            const duration = parseInt(step.duration);
            const to = parseInt(step.to);
            const from = parseInt(step.from);

            switch (step.attribute) {
                /* All alpha opacity transitions in the XML are either from 0 to 0, 0 to 100, or 100 to 100 which
                means we can basically treat the image in the animation step as visible or invisible. */
                case 'alpha':
                    if (from === 0 && to === 0) { // Image not visible.
                        stepsOb.opacity = 0;
                        stepsOb.duration = duration;
                    } else if (from !== to) { // Opacity phase shift.
                        stepsOb.transition = `opacity ${duration}s ease-in`;
                        stepsOb.duration = 0;
                        stepsOb.opacity = 1;
                    } else if (from === to) { // Idle - wait time.
                        stepsOb.duration = duration;
                        stepsOb.opacity = 1;
                    }
                    break;
                case 'x':
                    stepsOb.transform = this.translateX(from, to);
                    stepsOb.duration = 0;
                    // Duration is used here in the context of transition timing.
                    stepsOb.transition = `${duration}s`;
                    break;
                default:
                    throw new Error('Unsupported animation attribute');
            }

            stepsList.push(stepsOb);
        });

        return stepsList;
    }

    delay(stepData) {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                this.setState(stepData);
                resolve();
            }, stepData.duration * 1000);

            // Keep track so we can clear later.
            this.state.timeouts.push(timeout);
        });
    }

    render() {
        const {moduleURL = ''} = this.props;
        const {x = 0, y = 0, src = '', visible = '0', alpha = '', bgalpha = '', zIndex = null, width = '',
            height = '', transition = null, transform = null, opacity = 1, start = '0'} = this.state;

        const divStyle = {
            top: `${y}px`,
            left: `${x}px`,
            display: parseInt(visible) ? 'block' : 'none',
        };

        if (zIndex !== null) {
            divStyle.zIndex = zIndex;
        }

        // xml files could use either alpha or bgalpha to represent opacity
        if (bgalpha || alpha) {
            if (alpha){
                divStyle.opacity = alpha;
            } else if (bgalpha){ // if we need to set image opacity, we divide the XML value by 100
                divStyle.opacity = (bgalpha === '0' ? 0 : parseInt(bgalpha) / 100);
            }
        }

        const imageStyle = {opacity};
        if (width) {
            // Width may not always be defined, so it isn't added right at style initialization.
            imageStyle.width = `${width}px`;
        }
        if (height) {
            // Height may not always be defined, so it isn't added right at style initialization.
            imageStyle.height = `${height}px`;
        }

        if (transition !== null) {
            imageStyle['transition'] = transition;
        }
        if (transform !== null) {
            imageStyle['transform'] = transform;
        }

        return (
            <div className="node animated-image-node" style={divStyle} >
                <Easyfa ref={(aPng) => { this.aPng=aPng; }}
                        style={imageStyle}
                        src={`${moduleURL}/media/${src}`}
                        onLoad={start === '1' ? this.startAnimation.bind(this) : null}
                        onLoopEnd={this.onAnimationIterationComplete.bind(this)}
                        onEnd={this.onAnimationIterationComplete.bind(this)} // onEnd runs only when a loop==='0' apng finishes
                    // onLoopStart={()=>console.log('loop start')}
                    // autoplay={start === '1'}
                />
            </div>
        );
    }
}


AnimatedImageNode.propTypes = {
    node: PropTypes.object.isRequired,
    multiAnimatedImageNodes: PropTypes.bool.isRequired,
};

export default withRouter(AnimatedImageNode);



// WEBPACK FOOTER //
// ./src/js/components/app/jre/elements/animation_image_node.js